import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { Form } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import config from "../../Config";
import Examination_navbar from "./Examination_navbar";
import SessionsDropDown from "../Shared/SessionsDropDown";
import WingsDropDown from "../Shared/WingsDropDown";
import ClassesDropDown from "../Shared/ClassesDropDown";
import SectionDropDown from "../Shared/SectionDropDown";
import { scrollToTop } from "../Shared/ScrollOnTop";

function PublishResultCard() {
  const [searchParams] = useSearchParams();
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [SessionID, setSessionID] = useState(
    parseInt(localStorage.getItem("DefaultSession"), 10) || 0
  );
  const [resultCards, setResultCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [validated, setValidated] = useState(false);
  const [ResultCardID, setResultCardID] = useState(
    parseInt(searchParams.get("ID"), 10) || 0
  );
  const [WingID, setWingID] = useState(0);
  const [ClassID, setClassID] = useState(0);
  const [SectionID, setSectionID] = useState(0);
  const [publishStatus, setPublishStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const loadPublishStatus = useCallback(() => {
    if (!ResultCardID) {
      setPublishStatus(null);
      return;
    }

    setLoadingStatus(true);
    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetPublishStatus",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        ID: ResultCardID,
        Wing_Id: parseInt(WingID, 10) || 0,
        Class_Id: parseInt(ClassID, 10) || 0,
        Section_Id: parseInt(SectionID, 10) || 0,
      },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setPublishStatus(response.data.Status || null);
        } else {
          setPublishStatus(null);
        }
        setLoadingStatus(false);
      })
      .catch(() => {
        setPublishStatus(null);
        setLoadingStatus(false);
      });
  }, [AccessKey, UserID, MemberType, ResultCardID, WingID, ClassID, SectionID]);

  useEffect(() => {
    document.title = "Publish Result Card";
  }, []);

  useEffect(() => {
    loadPublishStatus();
  }, [loadPublishStatus]);

  useEffect(() => {
    if (!SessionID) {
      setResultCards([]);
      return;
    }

    setLoadingCards(true);
    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetResultCards",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, SessionID },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setResultCards(response.data.ListofRecords || []);
        } else {
          setResultCards([]);
        }
        setLoadingCards(false);
      })
      .catch(() => {
        setResultCards([]);
        setLoadingCards(false);
      });
  }, [SessionID]);

  const handlePublish = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    if (form.checkValidity() === false || !ResultCardID) {
      setValidated(true);
      if (!ResultCardID) {
        toast.error("Select a result card to publish.");
      }
      return;
    }

    setPublishing(true);
    axios({
      method: "post",
      url: config.base_url + "ResultCard/PublishResultCard",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        ID: ResultCardID,
        Wing_Id: parseInt(WingID, 10) || 0,
        Class_Id: parseInt(ClassID, 10) || 0,
        Section_Id: parseInt(SectionID, 10) || 0,
      },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          toast.success(response.data.status_message);
          loadPublishStatus();
        } else {
          toast.error(response.data.status_message || "Publish failed.");
        }
        setPublishing(false);
      })
      .catch(() => {
        setPublishing(false);
        toast.error("Network error.");
      });

    setValidated(true);
  };

  const printLink =
    ResultCardID > 0
      ? `/ExaminationSystem/PrintPublishedResultCard?ID=${ResultCardID}&Class_Id=${ClassID || 0}&Section_Id=${SectionID || 0}`
      : "/ExaminationSystem/PrintPublishedResultCard";

  const formatScopeLabel = () => {
    const parts = [];
    if (parseInt(WingID, 10) > 0) parts.push("selected wing");
    if (parseInt(ClassID, 10) > 0) parts.push("selected class");
    if (parseInt(SectionID, 10) > 0) parts.push("selected section");
    if (parts.length === 0) {
      return "all wings, classes, and sections";
    }
    return parts.join(", ");
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD MMM YYYY, h:mm A") : "-";
  };

  const selectedCard = resultCards.find((c) => c.ID === ResultCardID);

  return (
    <>
      <Examination_navbar />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/ExaminationSystem/ExaminationDashboard">
                      Examinations
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Publish Results
                  </li>
                </ol>
              </nav>
              <div className="page-title">Publish Result Card</div>
            </div>
            <div className="col-md-6">
              <div className="page-right-actions d-flex gap-2 justify-content-md-end">
                <Link
                  to={printLink}
                  className="button button-white button-icon"
                  onClick={scrollToTop}
                >
                  Print Published
                  <i className="ri-printer-line"></i>
                </Link>
                <Link
                  to="/ExaminationSystem/ManageResultCards"
                  className="button button-white button-icon"
                  onClick={scrollToTop}
                >
                  Result Cards
                  <i className="ri-list-check"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body">
              <Form noValidate validated={validated} onSubmit={handlePublish}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Session</label>
                      <select
                        className="form-select"
                        required
                        value={SessionID}
                        onChange={(e) => {
                          setSessionID(parseInt(e.target.value, 10) || 0);
                          setResultCardID(0);
                        }}
                      >
                        <option value="">Please Select</option>
                        <SessionsDropDown />
                      </select>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="form-group mb-3">
                      <label>Result Card</label>
                      <select
                        className="form-select"
                        required
                        value={ResultCardID}
                        onChange={(e) =>
                          setResultCardID(parseInt(e.target.value, 10) || 0)
                        }
                      >
                        <option value="">Please Select</option>
                        {loadingCards ? (
                          <option disabled>Loading...</option>
                        ) : (
                          resultCards.map((item) => (
                            <option key={item.ID} value={item.ID}>
                              {item.ResultCard_Code} - {item.Title}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group mb-3">
                      <label>Wing</label>
                      <select
                        className="form-select"
                        value={WingID}
                        onChange={(e) => {
                          setWingID(e.target.value);
                          setClassID(0);
                          setSectionID(0);
                        }}
                      >
                        <option value="0">All Wings</option>
                        <WingsDropDown selectedWingId={WingID} />
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group mb-3">
                      <label>Class</label>
                      <select
                        className="form-select"
                        value={ClassID}
                        onChange={(e) => {
                          setClassID(e.target.value);
                          setSectionID(0);
                        }}
                      >
                        <option value="0">All Classes</option>
                        <ClassesDropDown
                          WingID={WingID}
                          SessionID={SessionID}
                          selectedClassId={ClassID}
                        />
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group mb-3">
                      <label>Section</label>
                      <select
                        className="form-select"
                        value={SectionID}
                        onChange={(e) => setSectionID(e.target.value)}
                      >
                        <option value="0">All Sections</option>
                        <SectionDropDown
                          ClassID={ClassID}
                          selectedSectionId={SectionID}
                        />
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <div className="form-group mb-3 w-100">
                      {publishing ? (
                        <Spinner animation="border" role="status" />
                      ) : (
                        <button
                          type="submit"
                          className="button button-primary w-100"
                        >
                          Publish Results
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-muted mb-0">
                  Publishing creates promotion-safe snapshots for the selected
                  scope. Leave Wing, Class, or Section as &quot;All&quot; to
                  publish across multiple sections. Publishing again for the same
                  scope replaces the previous snapshot and increments the version.
                </p>
              </Form>
            </div>
          </div>

          {ResultCardID > 0 && (
            <div className="card mt-3 border-info">
              <div className="card-body">
                <h6 className="mb-3">Published status</h6>
                {loadingStatus ? (
                  <Spinner animation="border" size="sm" role="status" />
                ) : (
                  <>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="small text-muted">Result card</div>
                        <div>
                          {selectedCard
                            ? `${selectedCard.ResultCard_Code} - ${selectedCard.Title}`
                            : "—"}
                        </div>
                        <div className="small mt-1">
                          {publishStatus?.Is_Published ? (
                            <span className="text-success">
                              Published (card version {publishStatus.Publish_Version || 0})
                            </span>
                          ) : (
                            <span className="text-muted">Not published yet</span>
                          )}
                        </div>
                        {publishStatus?.Is_Published && publishStatus.Published_Date && (
                          <div className="small text-muted">
                            Last card publish: {formatDate(publishStatus.Published_Date)}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <div className="small text-muted">Selected scope</div>
                        <div className="text-capitalize">{formatScopeLabel()}</div>
                        {publishStatus?.Has_Published_In_Scope ? (
                          <>
                            <div className="small mt-1">
                              <strong>{publishStatus.Students_In_Scope}</strong> student(s) in{" "}
                              <strong>{publishStatus.Sections_In_Scope}</strong> section(s)
                            </div>
                            <div className="small text-muted">
                              Snapshot version {publishStatus.Scope_Publish_Version || "-"}
                              {publishStatus.Scope_Last_Published
                                ? ` · ${formatDate(publishStatus.Scope_Last_Published)}`
                                : ""}
                            </div>
                          </>
                        ) : (
                          <div className="small text-muted mt-1">
                            No published results in this scope yet.
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 d-flex align-items-end justify-content-md-end gap-2 flex-wrap">
                        <Link
                          to={printLink}
                          className={`button button-white button-sm ${
                            !publishStatus?.Has_Published_In_Scope ? "disabled" : ""
                          }`}
                          onClick={(e) => {
                            if (!publishStatus?.Has_Published_In_Scope) {
                              e.preventDefault();
                            } else {
                              scrollToTop();
                            }
                          }}
                        >
                          View published
                        </Link>
                        {parseInt(ClassID, 10) > 0 && parseInt(SectionID, 10) > 0 && (
                          <Link
                            to={`/ExaminationSystem/PrintPublishedStudentResultCards?ID=${ResultCardID}&ClassId=${ClassID}&SectionId=${SectionID}`}
                            className={`button button-white button-sm ${
                              !publishStatus?.Has_Published_In_Scope ? "disabled" : ""
                            }`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              if (!publishStatus?.Has_Published_In_Scope) {
                                e.preventDefault();
                              }
                            }}
                          >
                            Student cards
                          </Link>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PublishResultCard;
